import type { CSSProperties } from 'react'

/**
 * The Pocket brand mark.
 *
 * Supplied as a white-on-transparent SVG, which only works on a dark surface —
 * on the light theme it would be invisible. Every path is re-filled with
 * `currentColor` instead, so the mark takes the colour of the text around it:
 * near-black on light chrome, near-white on dark, with ONE asset and no
 * `dark:invert` filter hack (which the Frame mark still uses, and which
 * silently breaks the moment a logo stops being pure black or white).
 *
 * `wordmark` renders the full lockup; without it you get the mark alone.
 * Deliberately one component with a flag rather than two files: the Frame
 * sidebar learned the hard way that keeping a mark and a lockup as separate
 * assets means every brand change has to be made in several places, and the
 * brand changes shape depending on UI state.
 */

/** The two facets of the mark itself. */
const MARK_PATHS = [
  'M83.4324 101.661L73.459 118.779L57.9784 67.5677L69.9444 41.4897L122.592 25.575L112.05 45.9989L82.3766 54.969L74.1729 71.0298L83.4324 101.661Z',
  'M45.9591 22.6231L55.9325 5.50497L64.6132 34.2216L52.6472 60.2996L0 76.2143L10.5411 55.7904L40.215 46.8204L48.4186 30.7595L45.9591 22.6231Z',
]

/** The "pocket" letterforms, drawn to the right of the mark. */
const WORDMARK_PATHS = [
  'M156.296 27.648H165.512V39.04L176.904 27.648H200.84L212.616 39.424V79.616L200.84 91.392H176.264L165.768 82.176V118.528H156.296V27.648ZM196.744 82.944L203.144 76.672V42.368L196.744 36.096H179.464L165.768 49.792V71.808L178.056 82.944H196.744Z',
  'M229.266 79.616V39.424L241.042 27.648H273.81L285.586 39.424V79.616L273.81 91.392H241.042L229.266 79.616ZM269.714 82.944L276.114 76.672V42.368L269.714 36.096H245.138L238.738 42.368V76.672L245.138 82.944H269.714Z',
  'M302.266 79.616V39.424L314.042 27.648H344.762L356.538 39.424V47.232H347.066V42.368L340.666 36.096H318.138L311.738 42.368V76.672L318.138 82.944H340.666L347.066 76.672V71.808H356.538V79.616L344.762 91.392H314.042L302.266 79.616Z',
  'M371.921 0H381.393V54.016H396.369L415.057 27.648H425.553L404.049 58.368L426.833 91.392H416.337L396.369 62.464H381.393V91.392H371.921V0Z',
  'M434.516 79.616V39.424L446.292 27.648H478.42L490.324 39.424V62.336H443.988V76.672L450.388 82.944H474.324L480.724 76.672V71.808H490.196V79.616L478.42 91.392H446.292L434.516 79.616ZM480.852 54.144V42.368L474.452 36.096H450.388L443.988 42.368V54.144H480.852Z',
  'M513.306 79.616V35.968H501.786V27.648H513.562V6.912H522.778V27.648H542.746V35.968H522.778V76.672L529.178 83.072H542.746V91.392H525.082L513.306 79.616Z',
]

export function PocketMark({
  className, style, wordmark = false,
}: {
  className?: string
  style?: CSSProperties
  /** Include the wordmark beside the mark. */
  wordmark?: boolean
}) {
  const paths = wordmark ? [...MARK_PATHS, ...WORDMARK_PATHS] : MARK_PATHS
  return (
    <svg
      viewBox={wordmark ? '0 0 543 119' : '0 0 123 119'}
      className={className}
      style={style}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      {paths.map((d, i) => <path key={i} fill="currentColor" d={d} />)}
    </svg>
  )
}
